import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Store() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get(`${API}/products`);
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const categories = ['All', ...new Set(products.map(p => p.category))];
  const filteredProducts = filter === 'All' 
    ? products 
    : products.filter(p => p.category === filter);

  return (
    <main className="min-h-screen" data-testid="store-page">
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-forest overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-4xl mx-auto">
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-sm tracking-[0.3em] uppercase mb-6 text-white/80"
          >
            Shop
          </motion.p>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="font-serif text-4xl sm:text-5xl md:text-6xl leading-tight mb-8"
            data-testid="store-headline"
          >
            Nature-Inspired<br />
            <span className="italic font-normal">Products</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg text-white/90 max-w-2xl mx-auto leading-relaxed"
          >
            Sustainably sourced products from the Western Ghats. Every purchase directly 
            supports wilderness revival initiatives and regenerative livelihoods.
          </motion.p>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding bg-cream" data-testid="products-grid">
        <div className="container-custom">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-wrap justify-center gap-3 mb-16"
          >
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setFilter(category)}
                data-testid={`store-filter-${category.toLowerCase().replace(/\s+/g, '-')}`}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  filter === category
                    ? 'bg-forest text-white'
                    : 'bg-white text-foreground/70 hover:bg-forest/10'
                }`}
              >
                {category}
              </button>
            ))}
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-4 border-forest border-t-transparent rounded-full spinner" />
            </div>
          ) : (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {filteredProducts.map((product) => (
                <motion.div
                  key={product.id}
                  variants={fadeInUp}
                  layout
                  className="group bg-white rounded-2xl overflow-hidden card-hover cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                  data-testid={`product-${product.id}`}
                >
                  <div className="relative h-64 overflow-hidden bg-sand">
                    <img 
                      src={product.image}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-forest/90 text-white text-xs rounded-full">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-serif text-xl text-forest mb-2">{product.name}</h3>
                    <p className="text-foreground/70 text-sm line-clamp-2 mb-4">
                      {product.description}
                    </p>
                    {product.price_range && (
                      <p className="text-earth font-semibold text-lg mb-4">{product.price_range}</p>
                    )}
                    <Button 
                      variant="outline" 
                      className="w-full btn-outline text-xs py-3"
                      data-testid={`view-${product.id}`}
                    >
                      View Details
                    </Button>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          )}

          {/* Sustainability Note */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-16 bg-sand rounded-2xl p-8 text-center"
          >
            <h3 className="font-serif text-xl text-forest mb-4">Our Commitment</h3>
            <p className="text-foreground/70 text-sm max-w-2xl mx-auto">
              Every product is sustainably sourced with a commitment to Earth-first practices. 
              We urge you to reuse packaging and compost any waste. Together, we ensure a sustainable future.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Detail Modal */}
      <Dialog open={!!selectedProduct} onOpenChange={() => setSelectedProduct(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-cream" data-testid="product-modal">
          {selectedProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-serif text-2xl text-forest">
                  {selectedProduct.name}
                </DialogTitle>
              </DialogHeader>
              
              <div className="mt-4">
                <div className="relative h-72 rounded-xl overflow-hidden mb-6 bg-sand">
                  <img 
                    src={selectedProduct.image}
                    alt={selectedProduct.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-forest/90 text-white text-sm rounded-full">
                      {selectedProduct.category}
                    </span>
                  </div>
                </div>
                
                {selectedProduct.price_range && (
                  <p className="text-earth font-semibold text-2xl mb-4">{selectedProduct.price_range}</p>
                )}
                
                <p className="text-foreground/80 leading-relaxed mb-6">
                  {selectedProduct.description}
                </p>
                
                {selectedProduct.features && selectedProduct.features.length > 0 && (
                  <div className="mb-6">
                    <h4 className="font-serif text-lg text-forest mb-4">Key Features</h4>
                    <ul className="space-y-2">
                      {selectedProduct.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                          <span className="text-foreground/70">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                <div className="bg-forest/5 rounded-xl p-4 mb-6">
                  <p className="text-sm text-foreground/70">
                    <strong className="text-forest">Sustainability:</strong> Please reuse packaging and compost any waste. 
                    Every purchase supports wilderness revival initiatives.
                  </p>
                </div>
                
                <Link to="/contact" onClick={() => setSelectedProduct(null)}>
                  <Button className="btn-primary w-full" data-testid="modal-inquire-btn">
                    Inquire About This Product
                  </Button>
                </Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="section-padding bg-forest text-white">
        <div className="container-custom text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="font-serif text-3xl md:text-4xl mb-8">
              Looking for Something Specific?
            </h2>
            <p className="text-white/80 max-w-xl mx-auto mb-12 leading-relaxed">
              We can source specific products, bulk orders, or custom requirements. 
              Get in touch to discuss your needs.
            </p>
            <Link to="/contact">
              <Button 
                className="bg-white text-forest hover:bg-white/90 rounded-full px-10 py-5 font-semibold text-sm tracking-wider uppercase transition-all duration-300 hover:-translate-y-1"
                data-testid="store-contact-btn"
              >
                Contact Us <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
